import { Linkified } from '@livecontrol/core-ui';
import type {
  Message,
  RemovalReason,
  Reply,
  Watcher,
  WebSocketStatus
} from '@livecontrol/scheduler/model';
import { Account } from '@livecontrol/scheduler/model';
import { Scheduler } from '@livecontrol/scheduler/store';
import _ from 'lodash';
import { DateTime } from 'luxon';
import type { ReactElement, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import type { TabPill, TooltipPlacement } from '../../../components';
import {
  Modal,
  TabPills,
  Tag,
  Toggle,
  Tooltip,
  useConfirm,
  useVirtualRows
} from '../../../components';
import { Store, store } from '../../../store';
import type { ScheduledEvent } from '../../event-details';
import { useUpdateEventAdminFields } from '../../event-details';

type View = 'feed' | 'thread' | 'user' | 'viewers';

/** Which list the user panel was opened from, so Back returns there. */
type ListView = 'feed' | 'viewers';

/* `timeoutUser` takes minutes, so these map straight onto it. */
const TIMEOUT_OPTIONS = [5, 10, 30, 60];

const DEFAULT_TIMEOUT_MINUTES = 10;

/*
 * Starting heights for the windowed lists — a single-line row of each kind. Rows correct
 * themselves once rendered, so these only have to be close enough that the scrollbar does not
 * lurch on the first scroll.
 */
const ROW_ESTIMATE_PX = 76;

const VIEWER_ROW_ESTIMATE_PX = 66;

/*
 * A message and a reply differ in the API — the text lives in `message` on one and `reply` on the
 * other, and a reply is nested inside its parent's record — but a moderator treats them the same.
 * Normalising once here is what lets every row below be rendered by the same code.
 */
interface ChatItem {
  id: string;
  authorId: string;
  author: string;
  sentAt: number;
  text: string;
  deleted: boolean;
  /*
   * Why it left the feed, absent while it is still live. Removed items stay in the moderator's
   * list — struck through and restorable — and only disappear for viewers.
   */
  removedBy?: RemovalReason;
  /* Replies to this item, oldest first. Always empty on a reply: threads are one level deep. */
  replies: ChatItem[];
}

/*
 * One row of the live feed. Replies are hoisted out of their parent so the moderator sees them the
 * moment they arrive instead of having to open a thread; `parent` is what tells the two apart.
 */
interface FeedRow {
  message: ChatItem;
  parent?: ChatItem;
}

interface ChatViewer {
  id: string;
  name: string;
  status?: 'banned' | 'timeout';
}

const fullName = (first: string, last?: string): string =>
  [first, last].filter(Boolean).join(' ').trim() || 'Unknown viewer';

const fromReply = (reply: Reply): ChatItem => ({
  id: reply.pk,
  authorId: reply.senderId,
  author: fullName(reply.senderFirst, reply.senderLast),
  sentAt: reply.timestamp,
  text: reply.reply,
  deleted: reply.deleted ?? false,
  removedBy: reply.removedBy,
  replies: []
});

const fromMessage = (message: Message): ChatItem => ({
  id: message.pk,
  authorId: message.senderId,
  author: fullName(message.senderFirst, message.senderLast),
  sentAt: message.timestamp,
  text: message.message,
  deleted: message.deleted ?? false,
  removedBy: message.removedBy,
  replies: (message.replies ?? []).map(fromReply).sort((a, b) => a.sentAt - b.sentAt)
});

/*
 * `timeout_until` and `banned_at` are epoch seconds. A timeout that has run out is not a state
 * worth showing, so it is compared against the clock rather than merely being present.
 */
const fromWatcher = (watcher: Watcher): ChatViewer => {
  const timedOut = (watcher.timeout_until ?? 0) * 1000 > Date.now();

  return {
    id: watcher.id,
    name: fullName(watcher.name, watcher.last_name),
    status: watcher.banned_at ? 'banned' : timedOut ? 'timeout' : undefined
  };
};

/*
 * Messages and their replies in one chronological list, newest first. A reply is therefore shown
 * above the message it answers, which is the cost of surfacing it the moment it is written — the
 * arrow on the row is what points back at the thread.
 */
/*
 * Undoes the flattening for the API, which addresses a reply by its parent first and itself
 * second. A top-level message passes only its own id.
 */
const idsOf = (row: FeedRow): [messageId: string, replyId?: string] =>
  row.parent ? [row.parent.id, row.message.id] : [row.message.id];

const flattenFeed = (messages: ChatItem[]): FeedRow[] =>
  messages
    .flatMap((message) => [
      { message },
      ...message.replies.map((reply) => ({ message: reply, parent: message }))
    ])
    .sort((a, b) => b.message.sentAt - a.message.sentAt);

/*
 * The bubble is absolutely positioned and always in the DOM, so inside a scrolling list it
 * either gets clipped (hanging above the first row) or stretches the scroll area (hanging below
 * the last one). Pointing every row but the first upwards keeps the bubble over a sibling row,
 * which is already inside the scrollable area, so neither happens.
 */
const tooltipPlacement = (index: number): TooltipPlacement =>
  index === 0 ? 'bottom-right' : 'top-right';

/* How the socket's state reads in the header badge. */
const CONNECTION: Record<WebSocketStatus, { label: string; className: string }> = {
  connected: { label: 'Connected', className: 'cc-green' },
  connecting: { label: 'Connecting', className: 'cc-amber' },
  disconnected: { label: 'Not connected', className: 'cc-neutral' },
  error: { label: 'Connection error', className: 'cc-red' }
};

/* What a drill-down calls the view it came from, for the Back button. */
const BACK_LABELS: Record<View, string> = {
  feed: 'live feed',
  thread: 'thread',
  user: 'viewer',
  viewers: 'viewers'
};

/* "Aug 24 4:29 PM" — the API sends epoch millis. */
const formatSentAt = (timestamp: number): string => {
  const sent = DateTime.fromMillis(timestamp);

  return sent.isValid ? sent.toFormat('LLL d h:mm a') : '';
};

const initialsOf = (name: string): string =>
  name
    .split(' ')
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase();

/* Section title row, matching TicketsHeader above it. */
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 13px;
  min-height: 34px;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;

  .title {
    color: var(--white);
    font-weight: 700;
    font-size: 15px;
  }
`;

/* Same card and head treatment as the ticket list. */
const Card = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 11px;
  background: rgba(17, 27, 51, 0.32);
  overflow: hidden;
`;

const TableHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 11px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  flex-shrink: 0;

  .counts {
    display: inline-flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
  }

  .label {
    font-weight: 500;
    font-size: 11px;
    color: rgba(176, 186, 209, 0.6);
  }

  .value {
    margin-left: 5px;
    font-weight: 700;
    font-size: 11px;
    color: var(--white);
  }
`;

/*
 * Only the rows scroll, so the tabs and counters stay in view. The cap lives here rather than on
 * the card: with `max-height` on the card and a growing body, the bar showed even when the rows
 * fit. This is the same treatment the Notes and Contacts tabs use.
 */
const Body = styled.div`
  max-height: 340px;
  min-height:80px;
  overflow-y: auto;
`;

const Item = styled.div<{ $removed: boolean }>`
  display: flex;
  gap: 11px;
  align-items: flex-start;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background var(--transition);

  /*
   * A removed message is a record rather than live content, so it reads back: dimmed, struck
   * through, and without the hover highlight. The dimming stays off the actions, which still
   * have to be legible enough to restore the message.
   */
  ${({ $removed }): string =>
    $removed
      ? `
  .avatar,
  .content {
    opacity: 0.55;
  }

  .text {
    text-decoration: line-through;
  }
`
      : `
  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
`}

  /* Moderation stays out of the way until the row is hovered or focused into. */
  &:hover .actions,
  &:focus-within .actions {
    opacity: 1;
  }

  .content {
    flex: 1 1 0%;
    min-width: 0;
  }

  .line1 {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
  }

  .time {
    font-size: 11px;
    color: rgba(176, 186, 209, 0.55);
  }

  .text {
    margin-top: 4px;
    font-size: 13px;
    color: rgb(215, 220, 237);
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  /*
   * Underlined as well as coloured, so a link reads as one without leaning on colour. The
   * strike-through of a removed row still draws over it: a decoration set on an ancestor
   * propagates and a descendant cannot cancel it.
   */
  .text a {
    color: rgb(138, 165, 255);
    text-decoration: underline;
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    opacity: 0;
    transition: opacity var(--transition);
  }
`;

const Avatar = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-grid;
  place-items: center;
  font-size: 11px;
  font-weight: 800;
  flex-shrink: 0;
  background: rgba(159, 176, 255, 0.16);
  color: rgb(159, 176, 255);
`;

/* The person's name is the way into their panel, from either list. */
const NameButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-weight: 700;
  font-size: 13px;
  color: rgb(240, 242, 245);
  transition: color var(--transition);

  &:hover,
  &:focus {
    outline: none;
    color: rgb(159, 176, 255);
    text-decoration: underline;
  }
`;

/*
 * The way into a thread, from either end of it: the parent counts its replies, a reply shows the
 * arrow alone. Sits at the weight of the timestamp beside it so it does not compete with the text.
 */
const ThreadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: none;
  background: none;
  padding: 0;
  font-size: 11px;
  font-weight: 600;
  color: rgba(176, 186, 209, 0.55);
  transition: color var(--transition);

  &:hover,
  &:focus {
    outline: none;
    color: rgb(159, 176, 255);
  }
`;

const OffNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 13px 14px;
  background: rgba(242, 179, 61, 0.1);
  color: rgb(255, 210, 122);
  font-size: 12px;
  font-weight: 600;

  i {
    font-size: 13px;
    flex-shrink: 0;
  }
`;

const EmptyRow = styled.div`
  padding: 26px;
  text-align: center;
  font-weight: 500;
  font-size: 12px;
  color: rgba(176, 186, 209, 0.55);
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: rgba(176, 186, 209, 0.7);
  flex-shrink: 0;
  font-size: 12px;
  transition: all var(--transition);

  &:hover,
  &:focus {
    outline: none;
    color: rgb(159, 176, 255);
    border-color: rgba(255, 255, 255, 0.22);
  }

  &.danger {
    color: rgba(255, 138, 147, 0.75);
    border-color: rgba(255, 80, 95, 0.28);
  }

  &.danger:hover,
  &.danger:focus {
    color: rgb(255, 138, 147);
    border-color: rgba(255, 80, 95, 0.5);
    background: rgba(255, 80, 95, 0.1);
  }

  /* Undoing moderation — restore, unblock — reads as the reassuring counterpart of danger. */
  &.positive {
    color: rgba(127, 230, 184, 0.75);
    border-color: rgba(53, 211, 153, 0.28);
  }

  &.positive:hover,
  &.positive:focus {
    color: rgb(127, 230, 184);
    border-color: rgba(53, 211, 153, 0.5);
    background: rgba(53, 211, 153, 0.1);
  }
`;

/* Labelled variant for the user panel, where there is room to spell the action out. */
const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: rgba(176, 186, 209, 0.85);
  font-size: 12px;
  font-weight: 600;
  transition: all var(--transition);

  &:hover,
  &:focus {
    outline: none;
    color: rgb(159, 176, 255);
    border-color: rgba(255, 255, 255, 0.22);
  }

  &.danger:hover,
  &.danger:focus {
    color: rgb(255, 138, 147);
    border-color: rgba(255, 80, 95, 0.4);
  }

  &.positive:hover,
  &.positive:focus {
    color: rgb(127, 230, 184);
    border-color: rgba(53, 211, 153, 0.4);
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 10px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: rgba(176, 186, 209, 0.85);
  font-size: 11px;
  font-weight: 600;
  transition: all var(--transition);

  &:hover,
  &:focus {
    outline: none;
    color: rgb(159, 176, 255);
    border-color: rgba(255, 255, 255, 0.22);
  }
`;

/* Header of the drill-down: who this is, what state they are in, what can be done. */
const UserCard = styled.div`
  display: flex;
  gap: 13px;
  align-items: flex-start;
  padding: 15px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);

  .big-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: inline-grid;
    place-items: center;
    font-size: 14px;
    font-weight: 800;
    flex-shrink: 0;
    background: rgba(159, 176, 255, 0.16);
    color: rgb(159, 176, 255);
  }

  .content {
    flex: 1 1 0%;
    min-width: 0;
  }

  .line1 {
    display: flex;
    align-items: center;
    gap: 9px;
    flex-wrap: wrap;
  }

  .name {
    font-weight: 700;
    font-size: 15px;
    color: rgb(240, 242, 245);
  }

  .meta {
    margin-top: 5px;
    font-size: 11px;
    color: rgba(176, 186, 209, 0.55);
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 12px;
  }
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
`;

const FooterButton = styled.button`
  border-radius: 9px;
  font-weight: 600;
  font-size: 13px;
  padding: 9px 17px;
  transition: opacity 0.15s, background 0.15s;

  &:hover,
  &:focus {
    outline: none;
    opacity: 0.92;
  }
`;

const DurationGrid = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const DurationButton = styled.button<{ $selected: boolean }>`
  flex: 1 1 auto;
  min-width: 72px;
  padding: 10px 12px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  transition: all var(--transition);
  border: 1px solid
    ${({ $selected }): string =>
      $selected ? 'rgba(159, 176, 255, 0.5)' : 'rgba(255, 255, 255, 0.12)'};
  background: ${({ $selected }): string =>
    $selected ? 'rgba(159, 176, 255, 0.16)' : 'transparent'};
  color: ${({ $selected }): string =>
    $selected ? 'rgb(159, 176, 255)' : 'rgba(176, 186, 209, 0.85)'};

  &:hover,
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.22);
  }
`;


/* Shows the operator exactly what is about to disappear. */
const QuotedMessage = styled.blockquote`
  margin: 11px 0 0;
  padding: 9px 12px;
  border-left: 2px solid rgba(159, 176, 255, 0.5);
  border-radius: 0 8px 8px 0;
  background: rgba(17, 27, 51, 0.5);
  font-size: 13px;
  color: rgb(215, 220, 237);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const ModalText = styled.p`
  margin: 0;
  font-size: 13px;
  color: rgb(215, 220, 237);
`;

/* Same shape as the chat-off banner, in the danger palette. */
const ErrorNotice = styled(OffNotice)`
  background: rgba(255, 80, 95, 0.1);
  color: rgb(255, 138, 147);
`;

const ChatOffNotice = (): ReactElement => (
  <OffNotice>
    <i className='far fa-exclamation-circle' />
    Chat is turned off for viewers. Messages are hidden while chat is off.
  </OffNotice>
);

const StatusTag = ({ status }: { status?: 'banned' | 'timeout' }): ReactElement | null => {
  if (status === 'banned') {
    return (
      <Tag size='small' className='cc-red'>
        BLOCKED
      </Tag>
    );
  }

  if (status === 'timeout') {
    return (
      <Tag size='small' className='cc-amber'>
        TIMED OUT
      </Tag>
    );
  }

  return null;
};

/*
 * A scrolling list that keeps only the visible rows in the DOM. The panel loads the whole room in
 * one call, so without this a busy event mounts thousands of rows — each with its own tooltips —
 * for a viewport that shows five of them.
 */
interface ScrollListProps<T> {
  items: T[];
  /** Height to assume for a row before it has been measured. */
  estimate: number;
  keyOf: (item: T) => string;
  render: (item: T, index: number) => ReactElement;
  empty: ReactNode;
}

/* Declared rather than an arrow: in a .tsx file a generic arrow needs `<T,>` to not read as JSX. */
function ScrollList<T>({
  items,
  estimate,
  keyOf,
  render,
  empty
}: ScrollListProps<T>): ReactElement {
  const rows = useVirtualRows<HTMLDivElement>({ count: items.length, estimate });

  /*
   * The empty state keeps the same element and the same ref on purpose. The hook measures the
   * viewport once on mount, so handing it the element only after the first row arrives would leave
   * it believing the viewport is zero high forever.
   */
  return (
    <Body className='cc-scroll' ref={rows.scrollRef}>
      {items.length === 0 ? (
        empty
      ) : (
        /* The spacers stand in for the rows outside the window, so the scrollbar stays honest. */
        <div style={{ paddingTop: rows.paddingTop, paddingBottom: rows.paddingBottom }}>
          {items.slice(rows.start, rows.end).map((item, offset) => {
            const index = rows.start + offset;

            return (
              <div key={keyOf(item)} ref={rows.measure(index)}>
                {render(item, index)}
              </div>
            );
          })}
        </div>
      )}
    </Body>
  );
}

interface Props {
  scheduledEvent: ScheduledEvent;
}

export const LiveChat = ({ scheduledEvent }: Props): ReactElement => {
  const [view, setView] = useState<View>('feed');

  /*
   * `live_chat` is the event's own setting, so it seeds the toggle. Flipping it is still local:
   * see toggleChat.
   */
  const [enabled, setEnabled] = useState(scheduledEvent.live_chat ?? false);

  /* Which person the drill-down is showing, and which list to go back to. */
  const [selected, setSelected] = useState<{ id: string; from: ListView }>();

  /* Same for the thread drill-down. A thread can be opened from any of the other three views. */
  const [thread, setThread] = useState<{ id: string; from: View }>();

  const [updateAdminFields] = useUpdateEventAdminFields();

  const confirm = useConfirm();

  /* Who the timeout modal is about; the modal is open whenever this is set. */
  const [timingOut, setTimingOut] = useState<{ id: string; name: string }>();
  const [minutes, setMinutes] = useState(DEFAULT_TIMEOUT_MINUTES);

  /* Same for the ban modal, plus whether the ban takes their messages with it. */
  const [banning, setBanning] = useState<{ id: string; name: string }>();

  /* And its mirror image: who is being unblocked, and whether their messages come back too. */
  const [unblocking, setUnblocking] = useState<{ id: string; name: string }>();

  const { token } = Store.User.useIdentity();
  const { environment } = store((v) => _.pick(v, ['environment']));

  /*
   * The chat keys rooms by the webplayer's slug, not by the numeric event id — the same derivation
   * the player itself uses, or the panel would connect to a room nobody is in.
   */
  const roomId = Account.WebPlayer.makeEventSlug({ eventId: Number(scheduledEvent.id) });

  const {
    messages: rawMessages,
    watchers,
    currentUserId,
    status,
    error,
    timeoutUser,
    banUser,
    unbanUser,
    deleteMessage,
    restoreMessage
  } = Scheduler.AdminChat.useAdminChat({
    wsUrl: environment.SERVERLESS_WEBSOCKET,
    httpEndpoint: environment.SERVERLESS_URL,
    adminToken: token?.jwt ?? '',
    eventId: roomId,
    clientId: String(scheduledEvent.location?.user?.id ?? ''),
    autoConnect: true
  });

  const messages = useMemo(() => rawMessages.map(fromMessage), [rawMessages]);

  /*
   * The panel joins the room as an ordinary viewer named "Chat Admin", so it shows up in its own
   * list offering to ban and time out itself. Worse, every moderator registers under that same
   * identity, so acting on that row would hit all of them at once. A moderator is not a viewer.
   */
  const viewers = useMemo(
    () => watchers.filter((watcher) => watcher.id !== currentUserId).map(fromWatcher),
    [currentUserId, watchers]
  );

  const feed = useMemo(() => flattenFeed(messages), [messages]);

  /*
   * Removed messages stay in the feed, so the counters read off the flattened list — a removed
   * reply counts the same as a removed message. The filter's removals are counted apart from
   * everything a person decided, which is what makes the Auto-Moderated column worth having.
   */
  const deletedCount = feed.filter(
    (row) => row.message.deleted && row.message.removedBy !== 'system'
  ).length;

  const autoModeratedCount = feed.filter((row) => row.message.removedBy === 'system').length;
  const bannedCount = viewers.filter((viewer) => viewer.status === 'banned').length;

  /* How much each person has written, replies included — the viewers list has nothing else to show. */
  const messageCounts = useMemo(() => {
    const counts = new Map<string, number>();

    feed.forEach((row) => {
      counts.set(row.message.authorId, (counts.get(row.message.authorId) ?? 0) + 1);
    });

    return counts;
  }, [feed]);

  /* A ban belongs to the person, so every one of their messages has to show it. */
  const bannedIds = useMemo(
    () =>
      new Set(
        viewers.filter((viewer) => viewer.status === 'banned').map((viewer) => viewer.id)
      ),
    [viewers]
  );

  const toggleChat = (): void => {
    void (async (): Promise<void> => {
      const turningOff = enabled;

      const understood = await confirm({
        title: turningOff ? 'Turn off live chat' : 'Turn on live chat',
        message: turningOff
          ? 'This turns off live chat on the web player for all viewers of this event. Existing messages stay visible to moderators. Continue?'
          : "This turns live chat back on for all viewers of this event's web player. Continue?",
        tone: turningOff ? 'danger' : 'default'
      });

      if (!understood) {
        return;
      }

      const confirmed = await confirm({
        title: 'Confirm Change',
        message: turningOff
          ? 'Are you sure you want to turn off live chat for this event?'
          : 'Are you sure you want to turn live chat back on for this event?',
        tone: turningOff ? 'danger' : 'default',
        confirmLabel: turningOff ? 'Turn off Chat' : 'Turn on chat'
      });

      if (!confirmed) {
        return;
      }

      /* Optimistic: the switch answers at once and only steps back if the server refuses. */
      setEnabled(!turningOff);

      try {
        /*
         * A partial update, which is why this mutation and not Event.useEdit: that one replaces
         * the whole event, so a payload from here would blank out fields this panel never saw.
         * updateEventAdminFields pushes the web player manifest itself once the row is saved.
         */
        await updateAdminFields({ id: Number(scheduledEvent.id), liveChat: !turningOff });
      } catch {
        setEnabled(turningOff);

        await confirm({
          title: 'Could not change live chat',
          message: 'The setting was not saved. Check the connection and try again.',
          tone: 'danger',
          confirmLabel: 'OK'
        });
      }
    })();
  };

  const askTimeout = (id: string, name: string): void => {
    setMinutes(DEFAULT_TIMEOUT_MINUTES);
    setTimingOut({ id, name });
  };

  const confirmTimeout = (): void => {
    if (timingOut) {
      void timeoutUser(timingOut.id, minutes);
    }

    setTimingOut(undefined);
  };

  /*
   * A ban has no second decision to make any more, but it keeps its own modal: the confirm dialog
   * cannot say what banning does to what the person already wrote.
   */
  const askBan = (id: string, name: string): void => {
    setBanning({ id, name });
  };

  const confirmBan = (): void => {
    if (banning) {
      void banUser(banning.id);
    }

    setBanning(undefined);
  };

  const askUnblock = (id: string, name: string): void => {
    setUnblocking({ id, name });
  };

  const confirmUnblock = (): void => {
    if (unblocking) {
      void unbanUser(unblocking.id);
    }

    setUnblocking(undefined);
  };

  /* Restoring republishes something a moderator or the filter took down, so it is confirmed too. */
  const askRestore = (row: FeedRow): void => {
    const noun = row.parent === undefined ? 'message' : 'reply';

    void (async (): Promise<void> => {
      const confirmed = await confirm({
        title: row.parent === undefined ? 'Restore message' : 'Restore reply',
        message: (
          <>
            <ModalText>
              {row.message.removedBy === 'system'
                ? `This ${noun} was removed automatically. Restoring it makes it visible to everyone watching again.`
                : `This makes the ${noun} visible to everyone watching again.`}
            </ModalText>
            <QuotedMessage>{row.message.text}</QuotedMessage>
          </>
        ),
        confirmLabel: 'Restore'
      });

      if (confirmed) {
        restoreMessage(...idsOf(row));
      }
    })();
  };

  const askDelete = (row: FeedRow): void => {
    const isReply = row.parent !== undefined;

    void (async (): Promise<void> => {
      const confirmed = await confirm({
        title: isReply ? 'Delete reply' : 'Delete message',
        message: (
          <>
            <ModalText>
              This removes the {isReply ? 'reply' : 'message'} for everyone watching.
            </ModalText>
            <QuotedMessage>{row.message.text}</QuotedMessage>
          </>
        ),
        tone: 'danger',
        confirmLabel: 'Delete'
      });

      if (confirmed) {
        deleteMessage(...idsOf(row));
      }
    })();
  };

  const openUser = (id: string, from: ListView): void => {
    setSelected({ id, from });
    setView('user');
  };

  /*
   * A thread is reachable from the feed, the viewers list and the user panel, so Back has to
   * remember which of them it came from rather than assuming the feed.
   */
  const openThread = (id: string): void => {
    setThread({ id, from: view });
    setView('thread');
  };

  const pills: TabPill[] = useMemo(
    () => [
      {
        key: 'feed',
        label: 'Live Feed',
        count: feed.length,
        active: view === 'feed',
        onClick: (): void => {
          setView('feed');
        }
      },
      {
        key: 'viewers',
        label: 'Viewers',
        count: viewers.length,
        active: view === 'viewers',
        onClick: (): void => {
          setView('viewers');
        }
      }
    ],
    [feed.length, viewers.length, view]
  );

  const selectedViewer = viewers.find((viewer) => viewer.id === selected?.id);

  /* The user panel lists their replies too, each still carrying the parent it belongs to. */
  const userMessages = feed.filter((row) => row.message.authorId === selected?.id);

  /* `find` reports the miss honestly; indexing would be typed as always present here. */
  const anyMessageFromUser = feed.find((row) => row.message.authorId === selected?.id);

  /* The thread is addressed by the parent's id, so a reply row opens its parent's thread. */
  const threadParent = messages.find((message) => message.id === thread?.id);

  const selectedName =
    selectedViewer?.name ?? anyMessageFromUser?.message.author ?? 'Unknown viewer';

  const renderMessage = (row: FeedRow, index: number): ReactElement => {
    const { message, parent } = row;
    const blocked = bannedIds.has(message.authorId);

    /*
     * A banned author's content is hidden from viewers without anything being written to it, so
     * the panel shows it the way the room now sees it: gone. `message.deleted` stays untouched
     * underneath, which is what lets the row go back to normal the moment the ban is lifted.
     */
    const removed = message.deleted || blocked;
    const replyCount = message.replies.length;

    /*
     * A reply points at its parent's thread, a message with replies at its own, and a message
     * without replies has no thread to open. Inside a thread there is nowhere left to go.
     */
    const threadId =
      view === 'thread' ? undefined : parent?.id ?? (replyCount > 0 ? message.id : undefined);

    return (
      <Item key={message.id} $removed={removed}>
        <Avatar className='avatar'>{initialsOf(message.author)}</Avatar>
        <div className='content'>
          <div className='line1'>
            <NameButton
              type='button'
              onClick={(): void => {
                openUser(message.authorId, 'feed');
              }}
            >
              {message.author}
            </NameButton>
            <span className='time'>{formatSentAt(message.sentAt)}</span>
            {threadId !== undefined &&
              (parent === undefined ? (
                <ThreadButton
                  type='button'
                  onClick={(): void => {
                    openThread(threadId);
                  }}
                >
                  <i className='far fa-reply' />
                  {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                </ThreadButton>
              ) : (
                /* The arrow alone carries no meaning, so this one needs the tooltip. */
                <Tooltip content='View thread' placement={tooltipPlacement(index)}>
                  <ThreadButton
                    type='button'
                    aria-label='View thread'
                    onClick={(): void => {
                      openThread(threadId);
                    }}
                  >
                    <i className='far fa-reply' />
                  </ThreadButton>
                </Tooltip>
              ))}
            {/*
             * Neutral on purpose: a row can carry two of these at once, and colouring them turns
             * the feed into a traffic light. The colour lives in the viewers list instead.
             */}
            {message.deleted && message.removedBy !== 'system' && <Tag size='small'>DELETED</Tag>}
            {message.removedBy === 'system' && <Tag size='small'>AUTO-MODERATED</Tag>}
            {blocked && <Tag size='small'>BLOCKED</Tag>}
          </div>
          <div className='text'><Linkified text={message.text} /></div>
        </div>
        {/*
         * The message action keeps the first slot and the person action the second, whatever state
         * the row is in, so the buttons do not trade places between neighbouring rows.
         *
         * It keys off `deleted`, not off the struck-through look: a row hidden only because its
         * author is banned has nothing to restore — lifting the ban is what brings it back.
         */}
        <div className='actions'>
          {message.deleted ? (
            <Tooltip
              content={parent === undefined ? 'Restore message' : 'Restore reply'}
              placement={tooltipPlacement(index)}
            >
              <IconButton
                type='button'
                className='positive'
                aria-label={parent === undefined ? 'Restore message' : 'Restore reply'}
                onClick={(): void => {
                  askRestore(row);
                }}
              >
                <i className='far fa-undo' />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip
              content={parent === undefined ? 'Delete message' : 'Delete reply'}
              placement={tooltipPlacement(index)}
            >
              <IconButton
                type='button'
                aria-label={parent === undefined ? 'Delete message' : 'Delete reply'}
                onClick={(): void => {
                  askDelete(row);
                }}
              >
                <i className='far fa-trash-alt' />
              </IconButton>
            </Tooltip>
          )}
          {blocked ? (
            <Tooltip content='Unblock user' placement={tooltipPlacement(index)}>
              <IconButton
                type='button'
                className='positive'
                aria-label='Unblock user'
                onClick={(): void => {
                  askUnblock(message.authorId, message.author);
                }}
              >
                <i className='far fa-unlock-alt' />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip content='Ban author from this event' placement={tooltipPlacement(index)}>
              <IconButton
                type='button'
                className='danger'
                aria-label='Ban author'
                onClick={(): void => {
                  askBan(message.authorId, message.author);
                }}
              >
                <i className='far fa-ban' />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </Item>
    );
  };

  /*
   * The thread reads oldest to newest — the opposite of the feed. In a list you want the newest
   * thing first; in a conversation you want to read it in the order it was said.
   */
  const renderThread = (): ReactElement => {
    if (!enabled) {
      return <ChatOffNotice />;
    }

    if (threadParent === undefined) {
      return <EmptyRow>This thread is no longer available.</EmptyRow>;
    }

    const { replies } = threadParent;

    /* Not windowed, unlike the lists: a thread is bounded by one message's replies. */
    return (
      <Body className='cc-scroll'>
        {renderMessage({ message: threadParent }, 0)}
        {replies.length > 0 ? (
          replies.map((reply, index) =>
            renderMessage({ message: reply, parent: threadParent }, index + 1)
          )
        ) : (
          <EmptyRow>No replies yet.</EmptyRow>
        )}
      </Body>
    );
  };

  const renderViewer = (viewer: ChatViewer, index: number): ReactElement => {
    const written = messageCounts.get(viewer.id) ?? 0;

    return (
    <Item key={viewer.id} $removed={false}>
      <Avatar>{initialsOf(viewer.name)}</Avatar>
      <div className='content'>
        <div className='line1'>
          <NameButton
            type='button'
            onClick={(): void => {
              openUser(viewer.id, 'viewers');
            }}
          >
            {viewer.name}
          </NameButton>
          <StatusTag status={viewer.status} />
        </div>
        {/* The API gives watchers no join time, so the count is what there is to say here. */}
        <div className='text'>
          <span className='time'>
            {written} {written === 1 ? 'message' : 'messages'}
          </span>
        </div>
      </div>
      <div className='actions'>
        <Tooltip content='Time out' placement={tooltipPlacement(index)}>
          <IconButton
            type='button'
            aria-label='Time out viewer'
            onClick={(): void => {
              askTimeout(viewer.id, viewer.name);
            }}
          >
            <i className='far fa-clock' />
          </IconButton>
        </Tooltip>
        {viewer.status === 'banned' ? (
          <Tooltip content='Unblock user' placement={tooltipPlacement(index)}>
            <IconButton
              type='button'
              className='positive'
              aria-label='Unblock viewer'
              onClick={(): void => {
                askUnblock(viewer.id, viewer.name);
              }}
            >
              <i className='far fa-unlock-alt' />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip content='Ban from this event' placement={tooltipPlacement(index)}>
            <IconButton
              type='button'
              className='danger'
              aria-label='Ban viewer'
              onClick={(): void => {
                askBan(viewer.id, viewer.name);
              }}
            >
              <i className='far fa-ban' />
            </IconButton>
          </Tooltip>
        )}
      </div>
    </Item>
    );
  };

  return (
    <>
      <Header>
        <TitleGroup>
          <span className='title'>Web Player Live Chat</span>
          <Tag size='small' className={CONNECTION[status].className} icon='fas fa-circle' iconSize={7}>
            {CONNECTION[status].label}
          </Tag>
        </TitleGroup>
        <Toggle on={enabled} label='Enable web player chat' onChange={toggleChat} />
      </Header>

      <Card>
        {/* Above the tabs, because it explains why the lists below may be empty or stale. */}
        {error !== undefined && (
          <ErrorNotice>
            <i className='far fa-exclamation-triangle' />
            {error}
          </ErrorNotice>
        )}

        <TableHead>
          {view === 'thread' && (
            <BackButton
              type='button'
              onClick={(): void => {
                setView(thread?.from ?? 'feed');
              }}
            >
              <i className='far fa-arrow-left' />
              Back to {BACK_LABELS[thread?.from ?? 'feed']}
            </BackButton>
          )}
          {view === 'user' && (
            <BackButton
              type='button'
              onClick={(): void => {
                setView(selected?.from ?? 'feed');
              }}
            >
              <i className='far fa-arrow-left' />
              Back to {BACK_LABELS[selected?.from ?? 'feed']}
            </BackButton>
          )}
          {(view === 'feed' || view === 'viewers') && <TabPills small darker pills={pills} />}

          <div className='counts'>
            <span>
              <span className='label'>Deleted</span>
              <span className='value'>{deletedCount}</span>
            </span>
            <span>
              <span className='label'>Banned</span>
              <span className='value'>{bannedCount}</span>
            </span>
            <span>
              <span className='label'>Auto-Moderated</span>
              <span className='value'>{autoModeratedCount}</span>
            </span>
          </div>
        </TableHead>

        {view === 'thread' ? (
          renderThread()
        ) : view === 'user' ? (
          <>
            <UserCard>
              <span className='big-avatar'>{initialsOf(selectedName)}</span>
              <div className='content'>
                <div className='line1'>
                  <span className='name'>{selectedName}</span>
                  <StatusTag status={selectedViewer?.status} />
                </div>
                <div className='meta'>
                  {selectedViewer ? 'In this room' : 'No longer in this room'}
                  {' · '}
                  {userMessages.length} {userMessages.length === 1 ? 'message' : 'messages'}
                </div>
                <div className='actions'>
                  <ActionButton
                    type='button'
                    onClick={(): void => {
                      askTimeout(selected?.id ?? '', selectedName);
                    }}
                  >
                    <i className='far fa-clock' />
                    Time out
                  </ActionButton>
                  {selectedViewer?.status === 'banned' ? (
                    <ActionButton
                      type='button'
                      className='positive'
                      onClick={(): void => {
                        askUnblock(selected?.id ?? '', selectedName);
                      }}
                    >
                      <i className='far fa-unlock-alt' />
                      Unblock user
                    </ActionButton>
                  ) : (
                    <ActionButton
                      type='button'
                      className='danger'
                      onClick={(): void => {
                        askBan(selected?.id ?? '', selectedName);
                      }}
                    >
                      <i className='far fa-ban' />
                      Ban from event
                    </ActionButton>
                  )}
                </div>
              </div>
            </UserCard>
            {enabled ? (
              <ScrollList
                items={userMessages}
                estimate={ROW_ESTIMATE_PX}
                keyOf={(row): string => row.message.id}
                render={renderMessage}
                empty={<EmptyRow>This viewer has not written anything.</EmptyRow>}
              />
            ) : (
              <ChatOffNotice />
            )}
          </>
        ) : (
          <>
            {view === 'feed' && !enabled && <ChatOffNotice />}
            {/*
             * Distinct keys: the two lists sit at the same place in the tree, so without them
             * React reuses one instance and the viewers inherit the feed's measured row heights.
             */}
            {(view === 'viewers' || enabled) &&
              (view === 'feed' ? (
                <ScrollList
                  key='feed'
                  items={feed}
                  estimate={ROW_ESTIMATE_PX}
                  keyOf={(row): string => row.message.id}
                  render={renderMessage}
                  empty={<EmptyRow>No messages yet.</EmptyRow>}
                />
              ) : (
                <ScrollList
                  key='viewers'
                  items={viewers}
                  estimate={VIEWER_ROW_ESTIMATE_PX}
                  keyOf={(viewer): string => viewer.id}
                  render={renderViewer}
                  empty={<EmptyRow>Nobody has joined this chat yet.</EmptyRow>}
                />
              ))}
          </>
        )}
      </Card>

      <Modal
        open={unblocking !== undefined}
        onClose={(): void => {
          setUnblocking(undefined);
        }}
        title='Unblock user'
        width={420}
        footer={
          <FooterRow>
            <FooterButton
              className='cc-neutral'
              type='button'
              onClick={(): void => {
                setUnblocking(undefined);
              }}
            >
              Cancel
            </FooterButton>
            <FooterButton className='cc-green' type='button' onClick={confirmUnblock}>
              Unblock
            </FooterButton>
          </FooterRow>
        }
      >
        <ModalText>
          <strong>{unblocking?.name}</strong> will be able to post in this chat again, and
          everything they wrote becomes visible to viewers again.
        </ModalText>
      </Modal>

      <Modal
        open={banning !== undefined}
        onClose={(): void => {
          setBanning(undefined);
        }}
        title='Ban from event'
        width={420}
        footer={
          <FooterRow>
            <FooterButton
              className='cc-neutral'
              type='button'
              onClick={(): void => {
                setBanning(undefined);
              }}
            >
              Cancel
            </FooterButton>
            <FooterButton className='cc-red' type='button' onClick={confirmBan}>
              Ban
            </FooterButton>
          </FooterRow>
        }
      >
        <ModalText>
          <strong>{banning?.name}</strong> will not be able to post in this chat again, and
          everything they have written is hidden from viewers. They can keep watching.
        </ModalText>
      </Modal>

      <Modal
        open={timingOut !== undefined}
        onClose={(): void => {
          setTimingOut(undefined);
        }}
        title='Time out viewer'
        width={420}
        footer={
          <FooterRow>
            <FooterButton
              className='cc-neutral'
              type='button'
              onClick={(): void => {
                setTimingOut(undefined);
              }}
            >
              Cancel
            </FooterButton>
            <FooterButton className='cc-primary' type='button' onClick={confirmTimeout}>
              Time out for {minutes} min
            </FooterButton>
          </FooterRow>
        }
      >
        <ModalText>
          <strong>{timingOut?.name}</strong> will not be able to post for the selected time. They
          can keep watching, and their existing messages stay visible.
        </ModalText>
        <DurationGrid>
          {TIMEOUT_OPTIONS.map((option) => (
            <DurationButton
              key={option}
              type='button'
              $selected={option === minutes}
              onClick={(): void => {
                setMinutes(option);
              }}
            >
              {option} min
            </DurationButton>
          ))}
        </DurationGrid>
      </Modal>
    </>
  );
};
